import { Privacy, PrivacySelector } from "PFApp/components/activity_form/form_selectors/privacy_selector";
import { FIELD_AUDIENCES_KEY, FIELD_NAME_KEY } from "PFApp/constants/activity_form/activity_form_field_keys";
import { useWatchIsNew } from "PFApp/hooks/activity_form/multi_roles_form/use_watch_is_new";
import { MultiActivityForm, RoleFormKey } from "PFApp/types/activity_form/activity_form_schema";
import { InputFocusProps } from "PFComponents/table/interactive/use_cell_input_focus";
import { Audience } from "PFCore/types";
import { RefObject } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SidePanelCellFormSection } from "../side_panel_cell_form_section";

type PrivacyCellProps = {
  isPrivate?: boolean;
  roleFormKey: RoleFormKey;
  onChange: (isPrivate: boolean) => void;
  disabled?: boolean;
  error?: string;
  title?: string;
  inputFocusProps?: InputFocusProps;
  buttonRef?: RefObject<HTMLButtonElement>;
};

export const PrivacyCell = ({
  isPrivate = false,
  roleFormKey,
  onChange,
  disabled,
  error,
  title,
  inputFocusProps,
  buttonRef
}: PrivacyCellProps) => {
  const { t } = useTranslation();
  const { t: tActivities } = useTranslation("activities", { keyPrefix: "form.sections.privacy" });
  const { t: tBlueprints } = useTranslation("activities", { keyPrefix: "engagementBlueprint" });
  const { setValue } = useFormContext();
  const audiences = useWatch<MultiActivityForm, `${RoleFormKey}.${typeof FIELD_AUDIENCES_KEY}`>({
    name: `${roleFormKey}.${FIELD_AUDIENCES_KEY}`
  });
  const roleName = useWatch({ name: `${roleFormKey}.${FIELD_NAME_KEY}` });
  const isNew = useWatchIsNew(roleFormKey);

  const handlePrivacyChange = (id: Privacy) => {
    const updatedIsPrivate = id === Privacy.Private;
    const updatedAudiences = updatedIsPrivate ? audiences : [];
    onChange(updatedIsPrivate);
    setValue(`${roleFormKey}.${FIELD_AUDIENCES_KEY}`, updatedAudiences, { shouldDirty: true });
  };

  const handleAudienceChange = (newAudience: Audience[]) => {
    setValue(`${roleFormKey}.${FIELD_AUDIENCES_KEY}`, newAudience, { shouldDirty: true });
  };

  return (
    <SidePanelCellFormSection
      label={
        isPrivate
          ? [tActivities("private"), tActivities("audiencesCount", { count: audiences.length })]
          : tActivities("public")
      }
      sidePanelTitle={t("privacy")}
      disabled={disabled}
      error={error}
      editAriaLabel={tBlueprints("editPrivacyInRole", {
        roleName: roleName || tBlueprints("emptyRoleName")
      })}
      cellAriaLabel={title}
      inputFocusProps={inputFocusProps}
      buttonRef={buttonRef}
    >
      <PrivacySelector
        isPrivate={isPrivate}
        isNew={isNew}
        audience={audiences}
        onPrivateChange={handlePrivacyChange}
        onAudiencesChange={handleAudienceChange}
        isDisabled={disabled}
      />
    </SidePanelCellFormSection>
  );
};
